import { t } from '../../../../utils/i18n';
import { BreakdownTypes, type BreakdownTypesValues, type PaymentBreakdown } from '../../../../types/PaymentBreakdown';
import { type WidgetProps } from '../../../../types/WidgetProps';

export interface GetTransactionInfoParams {
  currency: string;
  paymentAmount: string;
  countryCode: string;
  sessionId: string;
  paymentBreakdown: PaymentBreakdown;
  requestShipping: boolean;
}

export const getTransactionInfo = ({
  currency,
  paymentAmount,
  countryCode,
  sessionId,
  paymentBreakdown,
  requestShipping,
}: GetTransactionInfoParams): google.payments.api.PaymentDataRequest['transactionInfo'] => {
  return {
    transactionId: sessionId,
    currencyCode: currency,
    countryCode,
    totalPrice: paymentAmount,
    totalPriceLabel: t('paymentSheet.paymentBreakdown.totalPrice'),
    totalPriceStatus: 'ESTIMATED',
    checkoutOption: 'DEFAULT',
    displayItems: getDisplayItems({ paymentBreakdown, requestShipping }),
  };
};

export const displayPaymentBreakdownItemType: PaymentBreakdown = {
  [BreakdownTypes.Discount]: 'DISCOUNT' as const,
  [BreakdownTypes.Shipping]: 'SHIPPING_OPTION' as const,
  [BreakdownTypes.Tax]: 'TAX' as const,
  [BreakdownTypes.ItemsTotal]: 'SUBTOTAL' as const,
} as const;

export const paymentBreakdownItemTranslationLabelKey: PaymentBreakdown = {
  [BreakdownTypes.Discount]: 'paymentSheet.paymentBreakdown.discount' as const,
  [BreakdownTypes.Shipping]: 'paymentSheet.paymentBreakdown.shipping' as const,
  [BreakdownTypes.Tax]: 'paymentSheet.paymentBreakdown.tax' as const,
  [BreakdownTypes.ItemsTotal]: 'paymentSheet.paymentBreakdown.itemsTotal' as const,
};

interface GetDisplayItemsParams {
  paymentBreakdown: WidgetProps['paymentBreakdown'];
  requestShipping: boolean;
}

export const getDisplayItems = ({
  paymentBreakdown,
  requestShipping,
}: GetDisplayItemsParams): google.payments.api.DisplayItem[] => {
  return Object.entries(paymentBreakdown).reduce((acc, [type, amount]: [BreakdownTypesValues, string | undefined]) => {
    const isDigitalProduct = !requestShipping;

    // Don't display shipping item for digital product
    if (isDigitalProduct && type === BreakdownTypes.Shipping) {
      return acc;
    }

    if (amount) {
      acc.push({
        label: t(paymentBreakdownItemTranslationLabelKey[type]),
        type: displayPaymentBreakdownItemType[type],
        price: amount,
        status: 'FINAL' as const,
      });
    }
    return acc;
  }, []);
};
